import { geocodeZipCode } from "./geocoding";
import { calculateDistance } from "../components/setup/utils";
import {
  normalizeStore,
  type StoreType,
} from "../components/BotFiltersOnboardingModal.utils";
import { normalizeStoreNumber } from "./userUtils";
import type { RetailerType } from "../config/retailers";

export const formatDistanceMiles = (miles: number) => `${miles.toFixed(1)} mi`;

const storeIdKeys = (storeId: string, retailer: string): string[] => {
  const clean = storeId.replace("-PENNY-ITEM", "").replace("-COUPON", "");
  const normalized = normalizeStoreNumber(clean, retailer as RetailerType);
  const stripped = clean.replace(/^0+/, "") || clean;
  const normalizedStripped = normalized.replace(/^0+/, "") || normalized;
  return [...new Set([storeId, clean, normalized, stripped, normalizedStripped].filter(Boolean))];
};

export const buildStoreGeoMap = (
  stores: unknown[] | null | undefined,
  retailer: string
): Map<string, { lat: number; long: number }> => {
  const map = new Map<string, { lat: number; long: number }>();
  if (!stores?.length) return map;

  for (const store of stores) {
    const normalized = normalizeStore(store as StoreType, retailer);
    const geo = normalized.normalizedGeo;
    if (!geo?.lat || !geo?.long || isNaN(geo.lat) || isNaN(geo.long)) continue;
    for (const key of storeIdKeys(normalized.normalizedId, retailer)) {
      map.set(key, geo);
    }
  }
  return map;
};

export async function enrichSearchResultsWithDistance<T extends { storeId?: string }>(
  results: T[],
  zipCode: string,
  stores: unknown[] | null | undefined,
  retailer: string
): Promise<(T & { distance?: string })[]> {
  if (!results.length || !/^\d{5}$/.test(zipCode.trim())) return results;

  const coords = await geocodeZipCode(zipCode.trim());
  if (!coords) return results;

  const geoMap = buildStoreGeoMap(stores, retailer);
  return results.map((result) => {
    if (!result.storeId) return result;

    let geo: { lat: number; long: number } | undefined;
    for (const key of storeIdKeys(result.storeId, retailer)) {
      geo = geoMap.get(key);
      if (geo) break;
    }
    if (!geo) return result;

    const miles = calculateDistance(coords.lat, coords.long, geo.lat, geo.long);
    return { ...result, distance: formatDistanceMiles(miles) };
  });
}
